"use client";

import { useAtomValue } from "jotai";
import { type FunctionComponent, memo, useCallback, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";

import { isShowTakeoverAdSlotAtom } from "scmp-app/components/advertisement/takeover-ad-slot/atoms";
import { useTypeformConfig } from "scmp-app/lib/typeform/hooks";

import CloseIcon from "./close-icon.svg";
import {
  CloseButton,
  FloatingButton,
  FloatingButtonWrapper,
  PopupContainer,
  PopupIframe,
  PopupOverlay,
  TooltipLabel,
} from "./styles";
import PollIcon from "./survey-icon.svg";

const POLL_AUTO_OPEN_PARAM = "poll_auto_open";

type Props = {
  entityId: string;
};

export const TypeformFloatingButtonComponent: FunctionComponent<Props> = ({ entityId }) => {
  const { isEnable, tooltipLabel, typeformLink } = useTypeformConfig(entityId);
  const isShowTakeoverAdSlot = Boolean(useAtomValue(isShowTakeoverAdSlotAtom));
  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"), false);

  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const takeoverOffset = isShowTakeoverAdSlot ? (isDesktopUp ? "120px" : "80px") : undefined;

  useEffect(() => {
    if (!isEnable || !typeformLink || isPopupOpen) return;

    const searchParameters = new URLSearchParams(window.location.search);
    if (searchParameters.get(POLL_AUTO_OPEN_PARAM) === "1") {
      setIsPopupOpen(true);

      const url = new URL(window.location.href);
      url.searchParams.delete(POLL_AUTO_OPEN_PARAM);
      window.history.replaceState(window.history.state, "", url.toString());
    }
  }, [isEnable, typeformLink, isPopupOpen]);

  useEffect(() => {
    document.body.style.overflow = isPopupOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isPopupOpen]);

  const handleOpenPopup = useCallback(() => {
    setIsPopupOpen(true);
  }, []);

  const handleClosePopup = useCallback(() => {
    setIsPopupOpen(false);
  }, []);

  if (!isEnable || !typeformLink) return null;

  return (
    <ErrorBoundary fallback={<></>}>
      <ClientSideSuspense fallback={<></>}>
        <FloatingButtonWrapper
          $hasTooltip={Boolean(tooltipLabel)}
          $takeoverOffset={takeoverOffset}
          onClick={handleOpenPopup}
        >
          {tooltipLabel && <TooltipLabel dangerouslySetInnerHTML={{ __html: tooltipLabel }} />}
          <FloatingButton aria-label="Open poll">
            <PollIcon />
          </FloatingButton>
        </FloatingButtonWrapper>
        {isPopupOpen && (
          <PopupOverlay>
            <PopupContainer>
              <CloseButton aria-label="Close poll" onClick={handleClosePopup}>
                <CloseIcon />
              </CloseButton>
              <PopupIframe src={typeformLink} title="Article Poll" />
            </PopupContainer>
          </PopupOverlay>
        )}
      </ClientSideSuspense>
    </ErrorBoundary>
  );
};

export const TypeformFloatingButton = memo(TypeformFloatingButtonComponent);
